// Tenant (specialty chatbot) configuration — derived from the SINGLE SOURCE OF
// TRUTH at the repo root, /tenants.json, imported via the '@tenants' Vite alias
// and bundled at build time. The backend reads the same file at runtime
// (apps/shared_backend/app/tenants.py). To add a specialty, edit /tenants.json
// only (and rebuild both images).
//
// The tenant is resolved from the first URL path segment (chat.ivelion.com/<tenant>)
// and selects branding (name), the WelcomeScreen categories, and
// the `tenant` value sent to the backend to select the specialty prompt.

import { Stethoscope, Brain, Beaker, Sparkles, HelpCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import tenantsData from "@tenants";

export interface SuggestionButton {
  label: string;
  message: string;
}

export interface WelcomeCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  suggestions: SuggestionButton[];
}

export interface TenantConfig {
  id: string;
  label: string;
  displayName: string;
  logo: string;
  categories: WelcomeCategory[];
}

// Map JSON icon names → Lucide components. Add new icons here as tenants use them.
const ICONS: Record<string, LucideIcon> = {
  Stethoscope,
  Brain,
  Beaker,
  Sparkles,
};

function resolveIcon(name: string): LucideIcon {
  return ICONS[name] ?? HelpCircle;
}

// Build the typed TenantConfig map from the raw JSON once at module load.
export const TENANTS: Record<string, TenantConfig> = Object.fromEntries(
  Object.entries(tenantsData.tenants).map(([id, raw]) => [
    id,
    {
      id,
      label: raw.label,
      displayName: raw.displayName,
      logo: raw.logo,
      categories: raw.categories.map((c) => ({
        id: c.id,
        label: c.label,
        icon: resolveIcon(c.icon),
        suggestions: c.suggestions,
      })),
    },
  ])
);

export const DEFAULT_TENANT = tenantsData.default;

/** Resolve the tenant id from a URL pathname's first segment, defaulting to DEFAULT_TENANT. */
export function resolveTenantId(pathname?: string): string {
  const path =
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const first = path.split("/").filter(Boolean)[0];
  return first && TENANTS[first] ? first : DEFAULT_TENANT;
}

/** True if the first path segment is a known tenant (used for 404 / routing). */
export function isKnownTenantSegment(pathname?: string): boolean {
  const path =
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const first = path.split("/").filter(Boolean)[0];
  return !!first && first in TENANTS;
}

/** Get the config for a tenant id (falls back to the default tenant). */
export function getTenantConfig(id?: string): TenantConfig {
  return TENANTS[id ?? resolveTenantId()] ?? TENANTS[DEFAULT_TENANT];
}
